import { Fragment, ReactNode, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import Routes from 'constants/Routes';
import {
  AssignmentCancelTypeEnum,
  CaseTypeEnum,
  DeliveredCarCategory,
  KPIBookedToChangeCaseRow,
  KPICancellationCaseRow,
} from 'api/model';
import {
  Axis,
  BarRow,
  Bars,
  Definitions,
  Delta,
  DetailHead,
  DetailList,
  Disclosure,
  Figures,
  Gap,
  Gaps,
  LeadRow,
  Legend,
  Note,
  Panel,
  PanelHead,
  ShiftDetailList,
  Stack,
  Table,
  TableWrap,
  TrendTable,
  WarnBox,
} from './reportStyles';
import { DAY_MS, ReportData, WeekComparison } from './useReportData';

/* ---------------- formattering ---------------- */

export const nf = (value: number, decimals = 0) =>
  value.toLocaleString('sv-SE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : `${nf(value, 1)} %`;

/** Timmar, med dygn i parentes så snart talet passerar ett dygn. */
export const hrs = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '–';
  if (value < 24) return `${nf(value, 1)} h`;
  return `${nf(value, 1)} h (${nf(value / 24, 1)} dygn)`;
};

/* ---------------- jämförelse mot föregående period ---------------- */

/** Vad som räknas som en förbättring. Ledtid och avbokningar ska ner, resten upp. */
export type GoodDirection = 'up' | 'down';

export interface Measure {
  key: string;
  label: string;
  /** Kort förklaring under etiketten i trendtabellen. */
  hint?: string;
  /**
   * Punkten i RM:s beställning (P1–P9). Bara för spårbarhet i kod och dokumentation — läsaren
   * ser inga nummer, rapporten ska gå att läsa utan att känna till beställningen.
   */
  point: string;
  goodWhen: GoodDirection;
  /** Null när måttet inte gick att räkna för perioden — då blir det inget delta heller. */
  value: (d: ReportData) => number | null;
  format: (value: number) => string;
  /**
   * Procentenheter för andelar och betyg, procent för antal och timmar. Ett mått som redan ÄR
   * en andel ska inte jämföras i procent av procenten — "76 % blev 79 %" är tre procentenheter,
   * inte fyra procent, och det är enheten ledningsgruppen räknar i.
   */
  deltaUnit: 'percent' | 'points' | 'absolute';
  formatDelta?: (delta: number) => string;
  /**
   * Ett antal som växer med periodens längd. Årsrapporten räknar om det till ett snitt per
   * vecka, så att det går att ställa bredvid veckornas tal; andelar, timmar och betyg gör det inte.
   */
  isCount?: boolean;
}

export const MEASURES: Measure[] = [
  {
    key: 'delivered',
    label: 'Levererade bilar',
    point: 'P1',
    goodWhen: 'up',
    value: (d) => d.deliveredCars.totalDeliveredCars,
    format: (v) => nf(v),
    deltaUnit: 'percent',
    isCount: true,
  },
  {
    key: 'leadTime',
    label: 'Bilar på transport',
    hint: 'median från upphämtning till Strängnäs',
    point: 'P9',
    goodWhen: 'down',
    value: (d) => (d.leadTime.cars > 0 ? d.leadTime.medianHours : null),
    format: (v) => `${nf(v, 1)} h`,
    deltaUnit: 'percent',
  },
  {
    key: 'truckFill',
    label: 'Fyllnadsgrad lastbil',
    hint: 'in och ut ur Strängnäs',
    point: 'P8',
    goodWhen: 'up',
    value: (d) => d.truckCoverage.totalFillRatePercentage ?? null,
    format: (v) => pct(v),
    deltaUnit: 'points',
  },
  {
    key: 'satisfaction',
    label: 'Kundnöjdhet',
    hint: 'snittbetyg av 5',
    point: 'P5',
    goodWhen: 'up',
    value: (d) => d.satisfaction.averageRating ?? null,
    format: (v) => nf(v, 2),
    deltaUnit: 'absolute',
    formatDelta: (v) => nf(v, 2),
  },
  {
    key: 'cancellations',
    label: 'Avbokningar',
    point: 'P4b',
    goodWhen: 'down',
    value: (d) => d.cancellations.totalCancelled,
    format: (v) => nf(v),
    deltaUnit: 'percent',
    isCount: true,
  },
  {
    key: 'truckShare',
    label: 'Annonsbilar på lastbil',
    hint: 'andel av annonsvolymen',
    point: 'P3',
    goodWhen: 'up',
    value: (d) => (d.transportMix.totalCars > 0 ? d.transportMix.truckPercentage : null),
    format: (v) => pct(v),
    deltaUnit: 'points',
  },
  {
    key: 'utilization',
    label: 'Utnyttjande mot tak',
    hint: 'arbetade timmar av arbetstidstaket',
    point: 'P6',
    goodWhen: 'up',
    value: (d) => (d.utilization.fieldTesters > 0 ? d.utilization.utilizationOfCapPercentage : null),
    format: (v) => pct(v),
    deltaUnit: 'points',
  },
];

/**
 * Det <see cref="Measure" /> behöver bidra med för att ett delta ska gå att räkna och skriva ut:
 * åt vilket håll det är bra, i vilken enhet skillnaden uttrycks, och hur den formateras.
 *
 * Bryts ut ur `Measure` för att panelernas rader ska kunna jämföras med samma regler som
 * trendtabellens mått. En avbokningsorsak är inget nyckeltal och har varken `point` eller
 * `value` — men "ner är bra" och "jämförs i procent" gäller likafullt, och de reglerna ska bo
 * på ett ställe. Annars får raderna i P4 sin egen uppfattning om vad som är en förbättring.
 */
export type TrendSpec = Pick<Measure, 'goodWhen' | 'deltaUnit' | 'formatDelta'>;

/** Antal: fler är bra, jämförs i procent. Gäller P1:s ärendetyper och P3:s transportsätt. */
const COUNT_UP: TrendSpec = { goodWhen: 'up', deltaUnit: 'percent' };

/** Antal där färre är bra — avbokningar per orsak. */
const COUNT_DOWN: TrendSpec = { goodWhen: 'down', deltaUnit: 'percent' };

/** Andelar jämförs i procentenheter, aldrig i procent av procenten. */
const SHARE_UP: TrendSpec = { goodWhen: 'up', deltaUnit: 'points' };

/** Andelar där lägre är bra — ombokningsgraden. */
const SHARE_DOWN: TrendSpec = { goodWhen: 'down', deltaUnit: 'points' };

/** Betyg 1–5: skillnaden är betygssteg, inte procent. */
const RATING: TrendSpec = {
  goodWhen: 'up',
  deltaUnit: 'absolute',
  formatDelta: (v) => nf(v, 2),
};

export interface Trend {
  delta: number;
  /** Måttets egen enhet på deltat, färdigformaterat med tecken. */
  text: string;
  tone: 'good' | 'bad' | 'flat';
  arrow: '▲' | '▼' | '–';
}

/**
 * Skillnaden mellan två perioder, uttryckt i måttets egen enhet.
 *
 * Returnerar null när jämförelsen inte går att göra: saknas talet i någon av perioderna, eller
 * är föregående period noll på ett mått som jämförs i procent, finns det ingen procentuell
 * förändring att visa. Att skriva "+100 %" när man går från noll är att låtsas om en trend som
 * inte har något underlag.
 */
export const trendOf = (
  measure: TrendSpec,
  current: number | null,
  before: number | null
): Trend | null => {
  if (current === null || before === null) return null;

  const raw =
    measure.deltaUnit === 'percent'
      ? before === 0
        ? null
        : ((current - before) / Math.abs(before)) * 100
      : current - before;

  if (raw === null || !Number.isFinite(raw)) return null;

  // Avrunda till samma precision som deltat skrivs ut med. Görs det inte kan ett betyg som går
  // från 4,20 till 4,24 visas som "oförändrat" bredvid två tal som syns vara olika.
  const decimals = measure.deltaUnit === 'absolute' ? 2 : 1;
  const factor = 10 ** decimals;
  const rounded = Math.round(raw * factor) / factor;

  // "Oförändrat" betyder att rörelsen försvinner i avrundningen — alltså att den inte syns i
  // talet vi skriver ut. Allt som syns får ett tecken och en färg.
  const flat = rounded === 0;
  const improved = measure.goodWhen === 'up' ? rounded > 0 : rounded < 0;

  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  const magnitude = Math.abs(rounded);
  const body = measure.formatDelta
    ? measure.formatDelta(magnitude)
    : measure.deltaUnit === 'points'
      ? `${nf(magnitude, 1)} pe`
      : `${nf(magnitude, 1)} %`;

  return {
    delta: rounded,
    text: flat ? 'oförändrat' : `${sign}${body}`,
    tone: flat ? 'flat' : improved ? 'good' : 'bad',
    arrow: flat ? '–' : rounded > 0 ? '▲' : '▼',
  };
};

/** Trenden för ett mått, eller null när jämförelseperioden saknas. */
export const trendFor = (
  measure: Measure,
  data: ReportData,
  previous: ReportData | null
): Trend | null => (previous ? trendOf(measure, measure.value(data), measure.value(previous)) : null);

/**
 * Nycklarna i båda perioderna, unionade.
 *
 * Unionen är själva poängen. Panelernas rader byggs ur periodens egna datapunkter, så ett
 * segment som fanns förra perioden och saknas nu skulle annars falla ur tabellen helt — och då
 * syns det största tänkbara tappet, hela vägen ner till noll, som ingenting alls. En ärendetyp
 * som gick från 24 bilar till 0 är precis vad ledningsgruppen ska se.
 */
export const unionKeys = (
  current: Map<string, unknown>,
  previous: Map<string, unknown> | null
): string[] => Array.from(new Set([...current.keys(), ...(previous ? previous.keys() : [])]));

/** Panelerna som visar trend per rad tar båda perioderna. */
export interface PanelProps {
  data: ReportData;
  /** Null när jämförelsen inte efterfrågades eller inte gick att hämta — då visas inga deltan. */
  previous: ReportData | null;
}

/**
 * Radens delta som en liten etikett, eller ett streck när raden inte går att jämföra.
 *
 * Strecket är med flit inte tomt. En tom cell läses som "ingen förändring", och skillnaden mot
 * "går inte att jämföra" är hela skillnaden mellan ett tal man kan lita på och ett man inte kan.
 */
export const RowDelta = ({ trend }: { trend: Trend | null }) =>
  trend ? (
    <Delta $tone={trend.tone}>
      {trend.arrow} {trend.text}
    </Delta>
  ) : (
    <span style={{ color: 'var(--ink-3)' }}>–</span>
  );

/** Viktat medelvärde — perioderna har olika många bilar bakom sig. */
const weighted = (items: [number, number][]) => {
  const total = items.reduce((sum, [, w]) => sum + w, 0);
  return total > 0 ? items.reduce((sum, [v, w]) => sum + v * w, 0) / total : 0;
};

const Head = ({
  tag,
  title,
  unit,
}: {
  /** Bara för rubriker som inte är ett mått, som TREND. Måtten bär inga nummer för läsaren. */
  tag?: string;
  title: string;
  unit?: string;
}) => (
  <PanelHead>
    {tag ? <span className="tag">{tag}</span> : null}
    <h2>{title}</h2>
    <span className="spacer" />
    {unit ? <span className="unit">{unit}</span> : null}
  </PanelHead>
);

/* ---------------- P1 levererade bilar ---------------- */

/**
 * Bilar per ärendetyp. Bryts ut ur panelen för att jämförelseperioden ska räknas exakt likadant
 * — gör man om hopslagningen för hand vid sidan av blir trenden en skillnad i hur det räknades.
 */
const deliveredCarsByType = (d: ReportData) =>
  d.deliveredCars.dataPoints.reduce((map, p) => {
    map.set(p.reportRowName, (map.get(p.reportRowName) ?? 0) + p.deliveredCars);
    return map;
  }, new Map<string, number>());

export const DeliveredCarsPanel = ({ data, previous }: PanelProps) => {
  const r = data.deliveredCars;

  const current = deliveredCarsByType(data);
  const before = previous ? deliveredCarsByType(previous) : null;

  const rows = unionKeys(current, before)
    .map((name) => ({
      name,
      cars: current.get(name) ?? 0,
      // Saknas ärendetypen i jämförelseperioden är det noll bilar, inte ett okänt tal. Trenden
      // blir ändå tom, eftersom det inte finns någon procentuell förändring från noll.
      trend: before ? trendOf(COUNT_UP, current.get(name) ?? 0, before.get(name) ?? 0) : null,
    }))
    .sort((a, b) => b.cars - a.cars);

  const max = rows.length > 0 ? rows[0].cars : 1;
  const auction = r.dataPoints
    .filter((p) => p.category === DeliveredCarCategory.Auction)
    .reduce((sum, p) => sum + p.deliveredCars, 0);
  const other = r.otherCaseTypes.reduce((sum, o) => sum + o.deliveredCars, 0);

  return (
    <Panel $span={7}>
      <Head title="Levererade bilar per ärendetyp" unit="bilar" />
      <Bars>
        {rows.map((row) => (
          <BarRow key={row.name} $withDelta={!!before}>
            <div className="b-name">{row.name}</div>
            <div className="b-track">
              <div
                className="b-fill"
                style={{ width: `${max > 0 ? (row.cars / max) * 100 : 0}%` }}
              />
            </div>
            <div className="b-val">{nf(row.cars)}</div>
            {before ? (
              <div className="b-delta">
                <RowDelta trend={row.trend} />
              </div>
            ) : null}
          </BarRow>
        ))}
        <BarRow $total $withDelta={!!before}>
          <div className="b-name">Totalt</div>
          <div />
          <div className="b-val">{nf(r.totalDeliveredCars)}</div>
          {before ? (
            <div className="b-delta">
              <RowDelta
                trend={trendOf(
                  COUNT_UP,
                  r.totalDeliveredCars,
                  previous?.deliveredCars.totalDeliveredCars ?? null
                )}
              />
            </div>
          ) : null}
        </BarRow>
      </Bars>
      <Note>
        Ett ärende = en bil, räknad den period ärendet stängdes, vilket är när sista uppdraget
        godkändes. Auktion ({nf(auction)} bilar) redovisas som Inköp.{' '}
        <strong>Övrigt</strong> är {nf(other)} bilar i ärendetyper utanför de stora.
      </Note>
    </Panel>
  );
};

/* ---------------- P3 annons ---------------- */

const MIX_COLORS = ['var(--s1)', 'var(--s2)', 'var(--s3)'];

const transportMixByMode = (d: ReportData) =>
  d.transportMix.dataPoints.reduce((map, p) => {
    const current = map.get(p.modeName) ?? { mode: p.mode, cars: 0 };
    current.cars += p.cars;
    map.set(p.modeName, current);
    return map;
  }, new Map<string, { mode: number; cars: number }>());

export const TransportMixPanel = ({ data, previous }: PanelProps) => {
  const r = data.transportMix;

  const current = transportMixByMode(data);
  const before = previous ? transportMixByMode(previous) : null;

  const modes = unionKeys(current, before)
    .map((name) => {
      const row = current.get(name);
      const prior = before?.get(name);
      return {
        name,
        // Sorteringsnyckeln kan bara komma från den period raden finns i. Ett transportsätt som
        // bara fanns förra perioden faller annars först i listan med mode 0.
        mode: row?.mode ?? prior?.mode ?? 0,
        cars: row?.cars ?? 0,
        trend: before ? trendOf(COUNT_UP, row?.cars ?? 0, prior?.cars ?? 0) : null,
      };
    })
    .sort((a, b) => a.mode - b.mode);

  const total = modes.reduce((sum, m) => sum + m.cars, 0);

  return (
    <Panel $span={5}>
      <Head title="Annons: lastbil eller FT" unit={`${nf(r.totalCars)} bilar`} />
      {total === 0 ? (
        <Note>Inga annonsbilar i perioden.</Note>
      ) : (
        <>
          <Stack>
            {modes
              .filter((m) => m.cars > 0)
              .map((m, i) => (
                <div
                  key={m.name}
                  style={{ flex: m.cars, background: MIX_COLORS[i % MIX_COLORS.length] }}
                >
                  {pct((m.cars / total) * 100)}
                </div>
              ))}
          </Stack>
          <Legend>
            {modes.map((m, i) => (
              <span className="item" key={m.name}>
                <span
                  className="swatch"
                  style={{ background: MIX_COLORS[i % MIX_COLORS.length] }}
                />
                {m.name} · {nf(m.cars)}
                {before ? (
                  <>
                    {' '}
                    <RowDelta trend={m.trend} />
                  </>
                ) : null}
              </span>
            ))}
          </Legend>
        </>
      )}
      <Note>
        Lastbil totalt <strong>{pct(r.truckPercentage)}</strong>. Räknas per ärende, inte per
        uppdrag, så totalen stämmer mot annonsraden till vänster. Egen biltransport och inhyrd
        transportör hålls isär — två olika kostnadsbilder.
      </Note>
    </Panel>
  );
};

/* ---------------- P9 bilar på transport ---------------- */

/** Skalan kapas — enstaka bilar som stått i veckor skulle annars trycka ihop alla län. */
const LEAD_SCALE = 80;

/**
 * Ledtiden redovisas som **median**. Varken snittledtiden (`AverageHours`) eller längsta enskilda
 * ledtid (`MaxHours`) visas i panelen.
 *
 * Det är RM:s beslut och gäller tills vidare. Skälet är att båda talen bärs av samma handfull
 * bilar: en bil som blivit stående en vecka drar upp ett läns snitt med timmar utan att något
 * systematiskt förändrats, och att då ställa snittet bredvid medianen är att ge det utrymme igen
 * i den rad som valts för att slippa det. `MaxHours` finns kvar som förbehåll i "Underlagets
 * luckor" — där är den en varningslampa om underlaget, inte ett nyckeltal. Båda ligger kvar per
 * län och ärendetyp i Excel-underlaget för den som vill räkna på svansen.
 *
 * Körtiden i stapelns mörka del är fortfarande ett medelvärde per bil — etapptiderna har ingen
 * median i svaret — men den är ett annat mått än ledtiden och skevas inte av samma bilar.
 *
 * `useReportData` hämtar P9 i en enda grupp (årsgruppering). Medianer går inte att väga ihop, så
 * länens median blir exakt bara när perioden är en grupp.
 */
export const LeadTimePanel = ({ data }: { data: ReportData }) => {
  const r = data.leadTime;

  if (!r.strangnasFacilityResolved) {
    return (
      <Panel $span={12} $flag>
        <Head title="Median för bilar på transport" />
        <WarnBox>
          Logistikanläggningen i Strängnäs kunde inte hittas bland företagen, så talet går inte
          att räkna fram. Det betyder alltså <strong>inte</strong> att inga bilar kört — åtgärden
          är att märka anläggningen som logistikcenter.
        </WarnBox>
      </Panel>
    );
  }

  const counties = Array.from(
    r.dataPoints
      .reduce((map, p) => {
        const current = map.get(p.fromCountyName) ?? {
          cars: 0,
          drive: [] as [number, number][],
          median: [] as [number, number][],
        };
        current.cars += p.cars;
        current.drive.push([p.activeHours, p.cars]);
        current.median.push([p.medianHours, p.cars]);
        map.set(p.fromCountyName, current);
        return map;
      }, new Map<string, { cars: number; drive: [number, number][]; median: [number, number][] }>())
      .entries()
  )
    .map(([county, v]) => ({
      county,
      cars: v.cars,
      drive: weighted(v.drive),
      median: weighted(v.median),
    }))
    .sort((a, b) => b.cars - a.cars)
    .slice(0, 12);

  const categories = Array.from(
    r.byCategory
      .reduce((map, p) => {
        const current = map.get(p.categoryName) ?? {
          cars: 0,
          drive: [] as [number, number][],
          median: [] as [number, number][],
        };
        current.cars += p.cars;
        current.drive.push([p.activeHours, p.cars]);
        current.median.push([p.medianHours, p.cars]);
        map.set(p.categoryName, current);
        return map;
      }, new Map<string, { cars: number; drive: [number, number][]; median: [number, number][] }>())
      .entries()
  ).map(([name, v]) => ({
    name,
    cars: v.cars,
    drive: weighted(v.drive),
    median: weighted(v.median),
  }));

  /**
   * Stapeln är medianledtiden, hela raden ett enda tal. Körtiden ligger som mörk del och kapas
   * vid medianen i de fall den är längre — hellre en körtid som tar slut vid stapelns ände än en
   * stapel som blir längre än talet den illustrerar.
   */
  const row = (
    key: string,
    name: string,
    cars: number,
    median: number,
    drive: number,
    total = false
  ) => {
    const bar = Math.min(median, LEAD_SCALE) / LEAD_SCALE;
    const d = Math.min(drive, median, LEAD_SCALE) / LEAD_SCALE;
    const w = Math.max(bar - d, 0);
    return (
      <LeadRow key={key} $total={total}>
        <div className="l-name">
          {name}
          <span>{nf(cars)} bilar</span>
        </div>
        <div className="l-track">
          <div className="l-drive" style={{ width: `${d * 100}%` }} />
          <div className="l-wait" style={{ width: `${w * 100}%` }} />
        </div>
        <div className="l-val">
          {nf(median, 1)} h{median > LEAD_SCALE ? '›' : ''}
        </div>
      </LeadRow>
    );
  };

  return (
    <Panel $span={12}>
      <Head
        title="Median för bilar på transport, per startlän"
        unit="inköp & inbyte → Strängnäs"
      />
      <Bars>
        {counties.map((c) => row(c.county, c.county, c.cars, c.median, c.drive))}
        {row('total', 'Totalt', r.cars, r.medianHours, r.activeHours, true)}
      </Bars>

      <Axis>
        <div />
        <div className="ticks">
          <span>0 h</span>
          <span>20</span>
          <span>40</span>
          <span>60</span>
          <span>80 h +</span>
        </div>
        <div />
      </Axis>

      <Legend>
        <span className="item">
          <span className="swatch" style={{ background: 'var(--s1)' }} />
          Körtid — etappernas egen längd
        </span>
        <span className="item">
          <span className="swatch" style={{ background: 'var(--wait)' }} />
          Väntetid — bilen står still
        </span>
        <span className="item">› = stapeln är kapad vid 80 h</span>
      </Legend>

      {categories.length > 0 ? (
        <TableWrap style={{ marginTop: '18px' }}>
          <Table>
            <thead>
              <tr>
                <th>Per ärendetyp</th>
                <th className="num">Bilar</th>
                <th className="num">Median</th>
                <th className="num">Körtid per bil</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.name}>
                  <td>{c.name}</td>
                  <td className="num">{nf(c.cars)}</td>
                  <td className="num">{hrs(c.median)}</td>
                  <td className="num">{hrs(c.drive)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}

      <Note>
        Ledtid i klocktid, från första upphämtningen till avlämningen i Strängnäs. Helger och
        nätter räknas med — bilen är låst även då. <strong>Varje tal är medianen</strong>, alltså
        den mittersta bilen i länet: enstaka bilar som blivit stående i veckor flyttar den inte,
        och det är därför den står här ensam. Stapelns mörka del är körtiden, etappernas egen
        längd per bil, kapad vid medianen när den är längre; resten är väntetid, och det är den
        som går att planera bort. Vill man se svansen finns snitt och längsta ledtid kvar per län
        och ärendetyp i Excel-underlaget.
      </Note>

      <Note>
        <strong>Bilar som åkt på lastbil — underlaget är ofullständigt här.</strong> Deras klocka
        hämtas från lastbilen, eftersom uppdragets egna tider skrivs om i lastningsögonblicket och
        varken motsvarar upphämtningen eller ankomsten. Källan är lastbilsförarens egen last- och
        lossmarkering i ZäkraGo, och den görs inte konsekvent: en del av transporterna saknar
        markering eller har båda tiderna satta i samma ögonblick. För dem faller mätningen tillbaka
        på segmentets tider, och saknas även de redovisas bilen som en lucka i stället för att
        räknas som en snabb transport. Antalen står under Underlagets luckor.
        <br />
        <br />
        Det betyder att ledtiden för lastbilstransporterade bilar är <strong>försiktigt mätt men
        inte exakt</strong>, och att talet kommer att korrigeras uppåt i takt med att fler
        lastbilsförare använder appen. Arbetet med att göra markeringen möjlig för förare som kör
        över dygnsgränsen är gjort; nästa steg är att transportörernas terminaler matar in
        kördatan direkt i systemet. Jämför därför perioder med försiktighet: en förändring mellan
        två månader kan lika gärna bero på att fler förare markerat som på att transporterna
        faktiskt gått snabbare eller långsammare.
      </Note>
    </Panel>
  );
};

/* ---------------- P4 avbokningar ---------------- */

const cancellationsByReason = (d: ReportData) =>
  d.cancellations.dataPoints.reduce((map, p) => {
    map.set(p.cancelTypeName, (map.get(p.cancelTypeName) ?? 0) + p.cases);
    return map;
  }, new Map<string, number>());

/**
 * Bilarna bakom varje orsak, nycklade på samma orsaksnamn som tabellraderna.
 *
 * Namnet kommer ur datapunkterna och inte ur en egen lista i frontend: översättningen av
 * AssignmentCancelTypeEnum bor i backend (`CancelTypeName`), och två listor hade förr eller
 * senare sagt olika saker om samma orsak.
 */
const carsByReason = (d: ReportData) => {
  const nameOf = new Map<AssignmentCancelTypeEnum, string>(
    d.cancellations.dataPoints.map((p) => [p.cancelType, p.cancelTypeName] as const)
  );

  return d.cancellations.cases.reduce((map, row) => {
    const reason = nameOf.get(row.cancelType) ?? 'Okänd orsak';
    const list = map.get(reason);
    if (list) list.push(row);
    else map.set(reason, [row]);
    return map;
  }, new Map<string, KPICancellationCaseRow[]>());
};

/**
 * Regnumret som länk till ärendet, i en ny flik så att rapporten står kvar med sina utfällda
 * rader. Länken finns även när regnr saknas — det är just då man behöver ärendet för att se vad
 * bilen är.
 */
const CaseLink = ({ caseID, registrationNumber }: { caseID: number; registrationNumber: string }) => (
  <a
    className={registrationNumber ? 'case' : 'case missing'}
    href={generatePath(Routes.case.case, { id: String(caseID) })}
    target="_blank"
    rel="noreferrer"
  >
    {registrationNumber || 'regnr saknas'}
  </a>
);

/** Datumet kommer som ISO-sträng trots att den genererade typen säger Date. */
const shortDate = (value: Date) => new Date(value).toLocaleDateString('sv-SE');

export const CancellationsPanel = ({ data, previous }: PanelProps) => {
  const r = data.cancellations;

  // Flera orsaker kan stå utfällda samtidigt. Den som jämför två orsaker mot varandra ska
  // slippa fälla ihop den ena för att se den andra, och utskriften tar med det som är utfällt.
  const [expanded, setExpanded] = useState<string[]>([]);
  const toggle = (reason: string) =>
    setExpanded((open) =>
      open.includes(reason) ? open.filter((x) => x !== reason) : [...open, reason]
    );

  const cars = useMemo(() => carsByReason(data), [data]);

  const current = cancellationsByReason(data);
  const before = previous ? cancellationsByReason(previous) : null;

  const reasons = unionKeys(current, before)
    .map((reason) => ({
      reason,
      count: current.get(reason) ?? 0,
      // Färre avbokningar är en förbättring, så en orsak som går ner färgas grönt här medan
      // samma rörelse på levererade bilar hade färgats rött.
      trend: before ? trendOf(COUNT_DOWN, current.get(reason) ?? 0, before.get(reason) ?? 0) : null,
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <Panel $span={7}>
      <Head title="Avbokningar per orsak" unit="kundnära ärenden i perioden" />
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Orsak</th>
              <th className="num">Ärenden</th>
              <th className="num">Andel av avbokade</th>
              {before ? <th className="num">Förändring</th> : null}
            </tr>
          </thead>
          <tbody>
            {reasons.map((row) => {
              const list = cars.get(row.reason) ?? [];
              const isOpen = expanded.includes(row.reason);

              return (
                <Fragment key={row.reason}>
                  <tr>
                    <td>
                      <Disclosure
                        type="button"
                        onClick={() => toggle(row.reason)}
                        disabled={list.length === 0}
                        aria-expanded={isOpen}
                      >
                        <span className="caret" aria-hidden="true">
                          {isOpen ? '▾' : '▸'}
                        </span>
                        {row.reason}
                      </Disclosure>
                    </td>
                    <td className="num">{nf(row.count)}</td>
                    <td className="num">
                      {r.totalCancelled > 0 ? pct((row.count / r.totalCancelled) * 100) : '–'}
                    </td>
                    {before ? (
                      <td className="num">
                        <RowDelta trend={row.trend} />
                      </td>
                    ) : null}
                  </tr>
                  {isOpen ? (
                    <tr className="detail">
                      <td colSpan={before ? 4 : 3}>
                        <DetailHead>
                          {nf(list.length)} {list.length === 1 ? 'bil' : 'bilar'}
                        </DetailHead>
                        <DetailList>
                          {list.map((c) => (
                            <li key={`${c.cancelType}-${c.caseID}`}>
                              <CaseLink
                                caseID={c.caseID}
                                registrationNumber={c.registrationNumber}
                              />
                              <span className="date">
                                {shortDate(c.date)}
                                {c.assignments > 1 ? (
                                  <span className="legs"> ×{c.assignments}</span>
                                ) : null}
                              </span>
                            </li>
                          ))}
                        </DetailList>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            <tr className="total">
              <td>Avbokade totalt</td>
              <td className="num">{nf(r.totalCancelled)}</td>
              <td className="num">{pct(r.totalCancelledPercentage)} av bokade</td>
              {before ? (
                <td className="num">
                  <RowDelta
                    trend={trendOf(
                      COUNT_DOWN,
                      r.totalCancelled,
                      previous?.cancellations.totalCancelled ?? null
                    )}
                  />
                </td>
              ) : null}
            </tr>
            <tr className={r.rebooked === 0 ? 'warn' : ''}>
              <td>varav ombokade</td>
              <td className="num">{nf(r.rebooked)}</td>
              <td className="num">{pct(r.rebookedPercentage)}</td>
              {before ? (
                <td className="num">
                  {/* Ombokning är inte en avbokning man vill ha färre av — den är den goda
                      utgången när något ändå ställs in, så fler är bättre här. */}
                  <RowDelta
                    trend={trendOf(COUNT_UP, r.rebooked, previous?.cancellations.rebooked ?? null)}
                  />
                </td>
              ) : null}
            </tr>
          </tbody>
        </Table>
      </TableWrap>
      <Note>
        Räknas per ärende och bara på kundnära ärenden (inköp, hemleverans, inbyte; inte auktion).
        Ankras på den dag jobbet skulle ha utförts. Ett ärende med flera avbokade etapper står
        under Bomkörning om någon etapp bomkördes, annars under den senast bokades orsak;
        ombokade räknar ärenden där någon etapp ombokades. Klicka på en orsak för att se bilarna bakom den —{' '}
        <strong>en rad per bil</strong>, och <em> ×2</em> när bilen har flera avbokade
        etapper.{' '}
        {r.rebooked === 0 && r.totalCancelled > 0 ? (
          <>
            <strong>Noll ombokningar</strong> betyder inte att inget bokas om — orsaken
            <em> Ombokad</em> sätts inte i praktiken.{' '}
          </>
        ) : null}
        Nekade uppdrag ligger i Kvalitet → Nekade uppdrag, inte här.
      </Note>
    </Panel>
  );
};

/* ---------------- ändrade bokningstider ---------------- */

/**
 * Orsakerna i den ordning admin visar dem, under sitt ansvar. Fast ordning och inte störst
 * först: ledningsgruppen ska hitta Zäkras rader på samma ställe varje vecka.
 */
const BOOKED_TO_GROUPS: { name: string; color: string; reasons: string[] }[] = [
  { name: 'Kund', color: 'var(--s1)', reasons: ['Kundens önskemål'] },
  {
    name: 'Riddermark',
    color: 'var(--s2)',
    reasons: ['Ändrad i Ride', 'Bilen inte klar'],
  },
  {
    name: 'Zäkra',
    color: 'var(--s3)',
    reasons: ['Ingen fälttestare tillgänglig', 'Planering / samordning'],
  },
  { name: 'Övrigt', color: 'var(--ink-3)', reasons: ['Annat', 'Orsak saknas'] },
];

const SHIFT_CASE_TYPES: Record<number, string> = {
  [CaseTypeEnum.Purchase]: 'Inköp',
  [CaseTypeEnum.HomeDelivery]: 'Hemleverans',
  [CaseTypeEnum.TradeIn]: 'Inbyte',
};

/** "+1 d 2 h", "−45 min" — hur långt tiden flyttades. Minuterna utelämnas över ett dygn. */
export const formatShift = (minutes: number) => {
  const total = Math.round(Math.abs(minutes));
  if (total === 0) return '±0';
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  const mins = total % 60;
  const parts = [
    days > 0 ? `${days} d` : null,
    hours > 0 ? `${hours} h` : null,
    mins > 0 && days === 0 ? `${mins} min` : null,
  ].filter(Boolean);
  return `${minutes < 0 ? '−' : '+'}${parts.join(' ')}`;
};

const sumBy = <T,>(
  items: T[],
  key: (item: T) => string,
  value: (item: T) => number
) =>
  items.reduce((map, item) => {
    map.set(key(item), (map.get(key(item)) ?? 0) + value(item));
    return map;
  }, new Map<string, number>());

const shortDateTime = (value: Date) =>
  new Date(value).toLocaleString('sv-SE', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Hur många kundnära ärenden som får sin bokade tid flyttad, och varför. Ett ärende räknas en
 * gång, under sin senaste ombokning.
 *
 * Räknas på dagen ändringen gjordes, så en skickad vecka ändras inte i efterhand. Bara manuella
 * ändringar på kundnära uppdrag räknas; rättningar av felinlagd tid och automatiska ändringar
 * ligger i "Underlagets luckor". Medianen per orsak är hela periodens median och kommer från
 * `reasons`, eftersom den inte går att väga ihop ur dagarnas datapunkter.
 */
export const BookedToChangesPanel = ({ data, previous }: PanelProps) => {
  const r = data.bookedToChanges;
  const [expanded, setExpanded] = useState<string[]>([]);
  const toggle = (reason: string) =>
    setExpanded((open) =>
      open.includes(reason)
        ? open.filter((x) => x !== reason)
        : [...open, reason]
    );

  const byReason = sumBy(
    r.dataPoints,
    (p) => p.reasonName,
    (p) => p.cases
  );
  const beforeByReason = previous
    ? sumBy(
        previous.bookedToChanges.dataPoints,
        (p) => p.reasonName,
        (p) => p.cases
      )
    : null;

  // Medianen per orsak kommer från periodsammanställningen, inte från datapunkterna: de är per
  // dag, och medianer går inte att väga ihop.
  const medianByReason = new Map<string, number>(
    r.reasons.map((x) => [x.reasonName, x.medianShiftMinutes])
  );

  const cars = useMemo(() => {
    const map = new Map<string, KPIBookedToChangeCaseRow[]>();
    r.cases.forEach((c) =>
      map.set(c.reasonName, [...(map.get(c.reasonName) ?? []), c])
    );
    return map;
  }, [r.cases]);

  const delivered = data.deliveredCars.totalDeliveredCars;
  const per100 = delivered > 0 ? (r.totalCases / delivered) * 100 : null;
  const beforePer100 =
    previous && previous.deliveredCars.totalDeliveredCars > 0
      ? (previous.bookedToChanges.totalCases /
          previous.deliveredCars.totalDeliveredCars) *
        100
      : null;

  const groups = BOOKED_TO_GROUPS.map((g) => {
    const reasons = g.reasons
      .filter((name) => byReason.has(name) || beforeByReason?.has(name))
      .map((name) => ({
        name,
        count: byReason.get(name) ?? 0,
        trend: beforeByReason
          ? trendOf(
              COUNT_DOWN,
              byReason.get(name) ?? 0,
              beforeByReason.get(name) ?? 0
            )
          : null,
      }));
    const count = reasons.reduce((sum, x) => sum + x.count, 0);
    const before = beforeByReason
      ? g.reasons.reduce(
          (sum, name) => sum + (beforeByReason.get(name) ?? 0),
          0
        )
      : null;
    return {
      ...g,
      reasons,
      count,
      trend: before === null ? null : trendOf(COUNT_DOWN, count, before),
    };
  }).filter((g) => g.reasons.length > 0);

  const share = (count: number) =>
    r.totalCases > 0 ? pct((count / r.totalCases) * 100) : '–';
  const columns = previous ? 5 : 4;

  return (
    <Panel $span={12}>
      <Head title="Ändrade bokningstider" unit="ombokade ärenden i perioden" />
      <Figures>
        <div>
          <div className="value">{nf(r.totalCases)}</div>
          <div className="label">
            ombokade ärenden · {nf(r.totalChanges)} ändringar
          </div>
          {previous ? (
            <RowDelta
              trend={trendOf(
                COUNT_DOWN,
                r.totalCases,
                previous.bookedToChanges.totalCases
              )}
            />
          ) : null}
        </div>
        <div>
          <div className="value">{pct(r.rebookingRatePercentage)}</div>
          <div className="label">
            ombokningsgrad · {nf(r.rebookedCasesInPeriod)} av{' '}
            {nf(r.customerFacingCasesInPeriod)} ärenden
          </div>
          {previous ? (
            <RowDelta
              trend={trendOf(
                SHARE_DOWN,
                r.rebookingRatePercentage ?? null,
                previous.bookedToChanges.rebookingRatePercentage ?? null
              )}
            />
          ) : null}
        </div>
        <div>
          <div className="value">{per100 === null ? '–' : nf(per100, 1)}</div>
          <div className="label">per 100 levererade bilar</div>
          {previous ? (
            <RowDelta
              trend={
                per100 === null
                  ? null
                  : trendOf(COUNT_DOWN, per100, beforePer100)
              }
            />
          ) : null}
        </div>
        <div>
          <div className="value">
            {r.totalCases > 0 ? formatShift(r.medianShiftMinutes) : '–'}
          </div>
          <div className="label">
            median för flytten
            {r.totalCases > 0 ? ` · ${pct(r.laterPercentage)} senare` : ''}
          </div>
        </div>
      </Figures>

      {r.totalCases === 0 ? (
        <Note>Inga ombokningar i perioden.</Note>
      ) : (
        <>
          <Stack>
            {groups
              .filter((g) => g.count > 0)
              .map((g) => (
                <div
                  key={g.name}
                  style={{ flex: g.count, background: g.color }}
                >
                  {share(g.count)}
                </div>
              ))}
          </Stack>
          <Legend style={{ marginBottom: 14 }}>
            {groups.map((g) => (
              <span className="item" key={g.name}>
                <span className="swatch" style={{ background: g.color }} />
                {g.name} · {nf(g.count)}
              </span>
            ))}
          </Legend>
        </>
      )}

      {/* Raderna finns också när bara jämförelseperioden hade ombokningar — då visar tabellen
          nedgången. Utan någon av dem är tabellen tom och utelämnas. */}
      {groups.length > 0 ? (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Orsak</th>
                <th className="num">Ärenden</th>
                <th className="num">Andel</th>
                <th className="num">Median flytt</th>
                {previous ? <th className="num">Förändring</th> : null}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.name}>
                  <tr className="total">
                    <td>{g.name}</td>
                    <td className="num">{nf(g.count)}</td>
                    <td className="num">{share(g.count)}</td>
                    <td className="num">–</td>
                    {previous ? (
                      <td className="num">
                        <RowDelta trend={g.trend} />
                      </td>
                    ) : null}
                  </tr>
                  {g.reasons.map((row) => {
                    const list = cars.get(row.name) ?? [];
                    const isOpen = expanded.includes(row.name);
                    const median = medianByReason.get(row.name);
                    return (
                      <Fragment key={row.name}>
                        <tr>
                          <td>
                            <Disclosure
                              type="button"
                              onClick={() => toggle(row.name)}
                              disabled={list.length === 0}
                              aria-expanded={isOpen}
                            >
                              <span className="caret" aria-hidden="true">
                                {isOpen ? '▾' : '▸'}
                              </span>
                              {row.name}
                            </Disclosure>
                          </td>
                          <td className="num">{nf(row.count)}</td>
                          <td className="num">{share(row.count)}</td>
                          <td className="num">
                            {median === undefined ? '–' : formatShift(median)}
                          </td>
                          {previous ? (
                            <td className="num">
                              <RowDelta trend={row.trend} />
                            </td>
                          ) : null}
                        </tr>
                        {isOpen ? (
                          <tr className="detail">
                            <td colSpan={columns}>
                              <DetailHead>
                                {nf(list.length)}{' '}
                                {list.length === 1 ? 'bil' : 'bilar'}
                              </DetailHead>
                              <ShiftDetailList>
                                {list.map((c) => (
                                  <li key={`${c.reasonName}-${c.caseID}`}>
                                    <CaseLink
                                      caseID={c.caseID}
                                      registrationNumber={c.registrationNumber}
                                    />
                                    <span className="type">
                                      {SHIFT_CASE_TYPES[c.caseTypeID] ?? ''}
                                    </span>
                                    {/* Kundens län: från-länet på inköp och inbyte,
                                        till-länet på hemleverans. */}
                                    <span className="county">
                                      {c.customerCountyName ?? '(Okänt län)'}
                                    </span>
                                    <span className="date">
                                      {shortDateTime(c.fromBookedTo)} →{' '}
                                      {shortDateTime(c.toBookedTo)}
                                      {c.changes > 1 ? (
                                        <span className="legs">
                                          {' '}
                                          ×{c.changes}
                                        </span>
                                      ) : null}
                                    </span>
                                  </li>
                                ))}
                              </ShiftDetailList>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}
      <Note>
        Räknas per ärende, på dagen ändringen gjordes. En ombokning är en
        manuell flytt av bokat till-tiden på ett kundnära uppdrag (inköp,
        hemleverans, inbyte; inte auktion); ett ärende som flyttas flera gånger
        räknas en gång, under orsaken på sin senaste ombokning, och flytten är
        ärendets nettoflytt. <strong>Ombokningsgraden</strong> är andelen av de
        kundnära ärendena med bokad tid i perioden som ombokats någon gång,
        avbokade inräknade.
        Klicka på en orsak för att se bilarna —{' '}
        <strong>en rad per bil</strong>, från första till sista tiden i
        perioden, och
        <em> ×2</em> när samma bil flyttats flera gånger. Rättningar av
        felinlagd tid och automatiska ändringar står under Underlagets luckor.
      </Note>
    </Panel>
  );
};

/* ---------------- P5 kundnöjdhet ---------------- */

const RAMP = ['var(--o1)', 'var(--o2)', 'var(--o3)', 'var(--o4)', 'var(--o5)'];

const satisfactionByCategory = (d: ReportData) =>
  Array.from(
    d.satisfaction.dataPoints
      .reduce((map, p) => {
        const current = map.get(p.categoryName) ?? {
          responses: 0,
          sent: 0,
          rating: [] as [number, number][],
          dist: [0, 0, 0, 0, 0],
        };
        current.responses += p.responses;
        current.sent += p.surveysSent;
        current.rating.push([p.averageRating ?? 0, p.responses]);
        p.ratingDistribution.forEach((rd) => {
          if (rd.rating >= 1 && rd.rating <= 5) current.dist[rd.rating - 1] += rd.count;
        });
        map.set(p.categoryName, current);
        return map;
      }, new Map<string, { responses: number; sent: number; rating: [number, number][]; dist: number[] }>())
      .entries()
  )
    .map(([name, v]) => ({
      name,
      responses: v.responses,
      sent: v.sent,
      avg: weighted(v.rating),
      dist: v.dist,
    }))
    .filter((c) => c.responses > 0)
    .sort((a, b) => b.responses - a.responses);

export const SatisfactionPanel = ({ data, previous }: PanelProps) => {
  const categories = satisfactionByCategory(data);
  // Nyckeln är kategorinamnet. Kategorier utan svar är redan bortfiltrerade i båda perioderna,
  // så en kategori som saknar svar den här veckan har ingen rad att sätta ett delta på — och
  // det är rätt: ett snittbetyg av noll svar är inte noll, det saknas.
  const before = previous
    ? new Map(satisfactionByCategory(previous).map((c) => [c.name, c.avg]))
    : null;

  return (
    <Panel $span={5}>
      <Head title="Kundnöjdhet" unit="betyg 1–5" />
      {categories.length === 0 ? (
        <Note>Inga svar i perioden.</Note>
      ) : (
        categories.map((c) => (
          <div key={c.name} style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ flex: 1, fontSize: '13.5px', color: 'var(--ink-2)' }}>
                {c.name}
              </span>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '24px',
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                }}
              >
                {nf(c.avg, 2)}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--ink-3)' }}>av 5</span>
              {before ? (
                <RowDelta trend={trendOf(RATING, c.avg, before.get(c.name) ?? null)} />
              ) : null}
            </div>
            <div style={{ display: 'flex', height: '10px', gap: '2px', margin: '7px 0 5px' }}>
              {c.dist.map((count, i) =>
                count > 0 ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} style={{ flex: count, background: RAMP[i] }} />
                ) : null
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
              {nf(c.responses)} svar av {nf(c.sent)} utskick
              {c.sent > 0 ? ` (${pct((c.responses / c.sent) * 100)})` : ''}
            </div>
          </div>
        ))
      )}
      <Note>
        Redovisas bara på inköp och hemleverans; auktionsbilar ingår i inköp. Antal svar står
        bredvid snittet — svarsfrekvensen är låg, så en enskild vecka säger lite.
      </Note>
    </Panel>
  );
};

/* ---------------- P6 resursutnyttjande ---------------- */

export const UtilizationPanel = ({ data }: { data: ReportData }) => {
  const r = data.utilization;

  if (!r.fieldTesterRoleResolved) {
    return (
      <Panel $span={12} $flag>
        <Head title="Resursutnyttjande mot arbetstidstak" />
        <WarnBox>
          Rollen <strong>Fälttestare</strong> kunde inte hittas, så det går inte att avgöra vem
          som ingår i taket. Utnyttjandet går alltså inte att räkna — det betyder{' '}
          <strong>inte</strong> att ingen arbetat.
        </WarnBox>
      </Panel>
    );
  }

  if (r.fieldTesters === 0) {
    return (
      <Panel $span={12} $flag>
        <Head title="Resursutnyttjande mot arbetstidstak" />
        <WarnBox>
          Inga fälttestare kom med i urvalet, så utnyttjandet går inte att räkna för perioden.
          Talet är alltså inte noll — det <strong>saknas</strong>.
          {r.fieldTestersWithoutEmploymentLevel > 0
            ? ` ${nf(r.fieldTestersWithoutEmploymentLevel)} fälttestare föll bort på saknad sysselsättningsgrad.`
            : ''}
          {r.fieldTestersExcludedBySupplier > 0
            ? ` ${nf(r.fieldTestersExcludedBySupplier)} fälttestare är dedikerade till en annan leverantör.`
            : ''}
          {r.fieldTestersInactiveInPeriod > 0
            ? ` ${nf(r.fieldTestersInactiveInPeriod)} fälttestare var inte i tjänst i perioden.`
            : ''}
        </WarnBox>
      </Panel>
    );
  }

  const units = Array.from(
    r.dataPoints
      .reduce((map, p) => {
        const key = p.businessUnitName || '(ingen affärsenhet)';
        const current = map.get(key) ?? { ft: 0, cap: 0, reported: 0, worked: 0 };
        current.ft = Math.max(current.ft, p.fieldTesters);
        current.cap += p.capHours;
        current.reported += p.reportedAvailableHours;
        current.worked += p.workedHours;
        map.set(key, current);
        return map;
      }, new Map<string, { ft: number; cap: number; reported: number; worked: number }>())
      .entries()
  )
    .map(([unit, v]) => ({ unit, ...v }))
    .sort((a, b) => b.cap - a.cap);

  return (
    <Panel $span={12}>
      <Head title="Resursutnyttjande mot arbetstidstak" unit="timmar" />
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Affärsenhet</th>
              <th className="num">FT</th>
              <th className="num">Tak</th>
              <th className="num">Anmält</th>
              <th className="num">Arbetat</th>
              <th className="num">Mot tak</th>
              <th className="num">Mot anmält</th>
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.unit}>
                <td>{u.unit}</td>
                <td className="num">{nf(u.ft)}</td>
                <td className="num">{nf(u.cap)}</td>
                <td className="num">{nf(u.reported)}</td>
                <td className="num">{nf(u.worked)}</td>
                <td className="num">{u.cap > 0 ? pct((u.worked / u.cap) * 100) : '–'}</td>
                <td className="num">
                  {u.reported > 0 ? pct((u.worked / u.reported) * 100) : '–'}
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>Totalt</td>
              <td className="num">{nf(r.fieldTesters)}</td>
              <td className="num">{nf(r.capHours)}</td>
              <td className="num">{nf(r.reportedAvailableHours)}</td>
              <td className="num">{nf(r.workedHours)}</td>
              <td className="num">{pct(r.utilizationOfCapPercentage)}</td>
              <td className="num">{pct(r.utilizationOfReportedPercentage)}</td>
            </tr>
          </tbody>
        </Table>
      </TableWrap>
      <Note>
        Taket är {nf(r.capHoursPerFullTimeWeek)} h per heltidsvecka gånger sysselsättningsgrad,
        minskat med godkänd frånvaro, och räknas bara för perioder där fälttestaren var i
        tjänst. Mot fälttestarnas egna anmälda tider ser utnyttjandet högre
        ut än mot arbetstidslagens tak — det är skillnaden mellan &quot;våra FT är fullbokade&quot;
        och &quot;vi köper {nf(r.capHours)} timmar och använder {nf(r.workedHours)}&quot;.
      </Note>
    </Panel>
  );
};

/* ---------------- P8 lastbil ---------------- */

/**
 * En rad per lastbil och riktning. De två riktningarna hålls isär: en full inkommande och en tom
 * utgående körning är inte "halvfullt", det är två olika problem.
 *
 * Körningen dateras på sitt hub-ögonblick — utgående på "Körning påbörjad", inkommande på
 * "Körning avslutad" — och alltså inte på ärendets stängningsdag som resten av rapporten. Ett
 * lastbilsärende kan löpa över flera dagar, och stängningsdagen lade då hela ärendets körningar
 * i samma vecka.
 *
 * Nyckeln är riktning + regnummer, så att jämförelseperioden träffar samma rad. Byter en lastbil
 * ägare eller regnummer blir det en ny rad — det är medvetet, för fyllnadsgraden hör till bilen
 * och inte till platsen i listan.
 */
const truckFillRows = (d: ReportData) =>
  d.truckCoverage.fillRates.reduce((map, p) => {
    const key = `${p.direction}|${p.registrationNumber}`;
    const current = map.get(key) ?? {
      direction: p.direction,
      directionName: p.directionName,
      truck: p.registrationNumber,
      runs: 0,
      capacity: 0,
      loaded: 0,
    };
    current.runs += p.runs;
    current.capacity += p.capacity;
    current.loaded += p.loadedCars;
    map.set(key, current);
    return map;
  }, new Map<string, { direction: number; directionName: string; truck: string; runs: number; capacity: number; loaded: number }>());

/** Fyllnadsgrad i procent, eller null när lastbilen inte kört — noll platser är inte noll procent. */
const fillRateOf = (row: { capacity: number; loaded: number } | undefined) =>
  row && row.capacity > 0 ? (row.loaded / row.capacity) * 100 : null;

export const TruckFillRatePanel = ({ data, previous }: PanelProps) => {
  const r = data.truckCoverage;

  if (!r.strangnasFacilityResolved) {
    return (
      <Panel $span={12} $flag>
        <Head title="Täckningsgrad lastbil" />
        <WarnBox>
          Logistikanläggningen i Strängnäs kunde inte hittas bland företagen, så fyllnadsgraden
          går inte att räkna. Det betyder alltså <strong>inte</strong> att inga lastbilar kört.
        </WarnBox>
      </Panel>
    );
  }

  const current = truckFillRows(data);
  const before = previous ? truckFillRows(previous) : null;

  const rows = unionKeys(current, before)
    .map((key) => {
      const row = current.get(key);
      const prior = before?.get(key);
      const shape = row ?? prior!;
      return {
        direction: shape.direction,
        directionName: shape.directionName,
        truck: shape.truck,
        runs: row?.runs ?? 0,
        capacity: row?.capacity ?? 0,
        loaded: row?.loaded ?? 0,
        trend: before ? trendOf(SHARE_UP, fillRateOf(row), fillRateOf(prior)) : null,
      };
    })
    .sort((a, b) => a.direction - b.direction || b.loaded - a.loaded);

  const summary = [
    {
      label: 'In till Strängnäs',
      runs: r.inboundRuns,
      capacity: r.inboundCapacity,
      loaded: r.inboundLoadedCars,
      fill: r.inboundFillRatePercentage,
    },
    {
      label: 'Ut från Strängnäs',
      runs: r.outboundRuns,
      capacity: r.outboundCapacity,
      loaded: r.outboundLoadedCars,
      fill: r.outboundFillRatePercentage,
    },
  ];

  return (
    <Panel $span={12}>
      <Head
        title="Täckningsgrad lastbil"
        unit="hur fulla lastbilarna in och ut var"
      />

      <Bars>
        {summary.map((sRow) => (
          <BarRow key={sRow.label}>
            <div className="b-name">{sRow.label}</div>
            <div className="b-track">
              <div className="b-fill" style={{ width: `${Math.min(sRow.fill ?? 0, 100)}%` }} />
            </div>
            <div className="b-val">{pct(sRow.fill)}</div>
          </BarRow>
        ))}
        <BarRow $total>
          <div className="b-name">Totalt</div>
          <div />
          <div className="b-val">{pct(r.totalFillRatePercentage)}</div>
        </BarRow>
      </Bars>

      <TableWrap style={{ marginTop: '18px' }}>
        <Table>
          <thead>
            <tr>
              <th>Riktning</th>
              <th>Lastbil</th>
              <th className="num">Körningar</th>
              <th className="num">Platser</th>
              <th className="num">Lastade</th>
              <th className="num">Fyllnadsgrad</th>
              {before ? <th className="num">Förändring</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={`${t.direction}-${t.truck}`}>
                <td>{t.directionName}</td>
                <td>{t.truck}</td>
                <td className="num">{nf(t.runs)}</td>
                <td className="num">{nf(t.capacity)}</td>
                <td className="num">{nf(t.loaded)}</td>
                <td className="num">
                  {t.capacity > 0 ? pct((t.loaded / t.capacity) * 100) : '–'}
                </td>
                {before ? (
                  <td className="num">
                    <RowDelta trend={t.trend} />
                  </td>
                ) : null}
              </tr>
            ))}
            <tr className="total">
              <td colSpan={2}>Totalt</td>
              <td className="num">{nf(r.totalRuns)}</td>
              <td className="num">{nf(r.totalCapacity)}</td>
              <td className="num">{nf(r.totalLoadedCars)}</td>
              <td className="num">{pct(r.totalFillRatePercentage)}</td>
              {before ? (
                <td className="num">
                  <RowDelta
                    trend={trendOf(
                      SHARE_UP,
                      r.totalFillRatePercentage ?? null,
                      previous?.truckCoverage.totalFillRatePercentage ?? null
                    )}
                  />
                </td>
              ) : null}
            </tr>
          </tbody>
        </Table>
      </TableWrap>

      <Note>
        Riktningen avgörs av körningens ändpunkter, och körningen dateras på när lastbilen stod i
        Strängnäs: utgående på avfärden därifrån, inkommande på ankomsten dit.{' '}
        <strong>
          Kapaciteten är antagen till {nf(r.assumedCapacityPerRun)} platser per körning
        </strong>{' '}
        eftersom lastbilarnas kapacitet är felkonfigurerad — {nf(r.runsWithConfiguredCapacity)} av{' '}
        {nf(r.totalRuns)} körningar har en satt kapacitet. Fyllnadsgraden är alltså delvis ett
        antagande, inte ett mätvärde. En körning som lastade fler bilar än de antagna platserna
        räknas som full, så fyllnadsgraden stannar på 100 %.
        {r.runsOutsideHub > 0
          ? ` ${nf(r.runsOutsideHub)} körningar varken började eller slutade på anläggningen och ingår inte.`
          : ''}
      </Note>
    </Panel>
  );
};

/* ---------------- kontrollsiffror ---------------- */

type GapKind = 'stop' | 'out' | 'ctx';

const GAP_LABEL: Record<GapKind, string> = {
  stop: 'blockerar',
  out: 'utanför talet',
  ctx: 'bakgrund',
};

export const GapsPanel = ({ data }: { data: ReportData }) => {
  const { deliveredCars, leadTime, utilization, truckCoverage, satisfaction, cancellations } =
    data;

  const surveysSent = satisfaction.dataPoints.reduce((sum, p) => sum + p.surveysSent, 0);

  const gaps: { value: string; text: ReactNode; measure: string; kind: GapKind }[] = [];

  if (cancellations.totalCancelled > 0 && cancellations.rebooked === 0)
    gaps.push({
      value: '0',
      text: `ombokningar registrerade på ${nf(cancellations.totalCancelled)} avbokningar — orsaken Ombokad används inte i praktiken`,
      measure: 'Avbokningar',
      kind: 'stop',
    });

  // Saknas rollen är det konfigurationen som brister, inte urvalet. Skrivs båda ut säger
  // rapporten två olika saker om samma nolla, och läsaren tror att kåren är tom.
  if (!utilization.fieldTesterRoleResolved)
    gaps.push({
      value: '—',
      text: 'resursutnyttjandet kunde inte räknas: rollen Fälttestare kunde inte hittas',
      measure: 'Utnyttjande',
      kind: 'stop',
    });
  else if (utilization.fieldTesters === 0)
    gaps.push({
      value: '—',
      text: 'resursutnyttjandet kunde inte räknas: noll fälttestare kom med i urvalet',
      measure: 'Utnyttjande',
      kind: 'stop',
    });

  if (deliveredCars.openCasesOlderThanPeriod > 0)
    gaps.push({
      value: nf(deliveredCars.openCasesOlderThanPeriod),
      text: 'öppna ärenden är äldre än perioden och syns aldrig i volymen',
      measure: 'Levererade bilar',
      kind: 'out',
    });

  if (leadTime.casesWithoutStrangnasDropOff > 0)
    gaps.push({
      value: nf(leadTime.casesWithoutStrangnasDropOff),
      text: 'inköps-/inbytesärenden nådde aldrig Strängnäs',
      measure: 'Bilar på transport',
      kind: 'out',
    });

  if (leadTime.casesWithoutUsableTimes > 0)
    gaps.push({
      value: nf(leadTime.casesWithoutUsableTimes),
      text: 'ärenden saknar användbara start- eller sluttider',
      measure: 'Bilar på transport',
      kind: 'out',
    });

  if (leadTime.carsWithoutMeasurableTime > 0)
    gaps.push({
      value: nf(leadTime.carsWithoutMeasurableTime),
      text:
        'bilar saknar mätbar tid — de redovisas här i stället för att räknas som ledtid noll',
      measure: 'Bilar på transport',
      kind: 'out',
    });

  if (leadTime.truckCarsMeasuredByFallback > 0)
    gaps.push({
      value: nf(leadTime.truckCarsMeasuredByFallback),
      text:
        'lastbilstransporterade bilar saknar förarens last-/lossmarkering och mäts på lastbilens grövre tider',
      measure: 'Bilar på transport',
      kind: 'ctx',
    });

  if (utilization.fieldTestersWithoutEmploymentLevel > 0)
    gaps.push({
      value: nf(utilization.fieldTestersWithoutEmploymentLevel),
      text: 'fälttestare saknar sysselsättningsgrad och ingår inte i taket',
      measure: 'Utnyttjande',
      kind: 'out',
    });

  if (utilization.fieldTestersExcludedBySupplier > 0)
    gaps.push({
      value: nf(utilization.fieldTestersExcludedBySupplier),
      text: 'fälttestare är dedikerade till en annan leverantör och ingår inte i taket',
      measure: 'Utnyttjande',
      kind: 'out',
    });

  if (utilization.fieldTestersInactiveInPeriod > 0)
    gaps.push({
      value: nf(utilization.fieldTestersInactiveInPeriod),
      text: 'fälttestare var inte i tjänst i perioden och bär därför inget tak',
      measure: 'Utnyttjande',
      kind: 'out',
    });

  if (truckCoverage.totalRuns > truckCoverage.runsWithConfiguredCapacity)
    gaps.push({
      value: nf(truckCoverage.totalRuns - truckCoverage.runsWithConfiguredCapacity),
      text: `körningar saknar satt kapacitet — fyllnadsgraden räknas på antagna ${nf(truckCoverage.assumedCapacityPerRun)} platser`,
      measure: 'Fyllnadsgrad lastbil',
      kind: 'stop',
    });

  if (truckCoverage.runsOutsideHub > 0)
    gaps.push({
      value: nf(truckCoverage.runsOutsideHub),
      text: 'lastbilskörningar varken började eller slutade i Strängnäs och ingår inte',
      measure: 'Fyllnadsgrad lastbil',
      kind: 'out',
    });

  if (truckCoverage.runsExcludedByFilters > 0)
    gaps.push({
      value: nf(truckCoverage.runsExcludedByFilters),
      text: 'lastbilskörningar föll bort på ärende- och leverantörsfiltret — leverantören sitter på företaget och kan saknas på lastbilsärendet',
      measure: 'Fyllnadsgrad lastbil',
      kind: 'out',
    });

  if (truckCoverage.runsWithoutDrivingEvent > 0)
    gaps.push({
      value: nf(truckCoverage.runsWithoutDrivingEvent),
      text: 'lastbilsrörelser saknar avfärds- eller ankomstevent och dateras på uppdragets tider — de räknas med i fyllnadsgraden men kan ligga i fel vecka. Till skillnad från talen ovan räknas rörelser: en rundtur är två',
      measure: 'Fyllnadsgrad lastbil',
      kind: 'ctx',
    });

  if (leadTime.carsWithoutFromCounty > 0)
    gaps.push({
      value: nf(leadTime.carsWithoutFromCounty),
      text: 'bilar saknar frånlän och ligger på "(Okänt län)"',
      measure: 'Bilar på transport',
      kind: 'ctx',
    });

  if (utilization.hoursAwaitingApproval > 0)
    gaps.push({
      value: `${nf(utilization.hoursAwaitingApproval)} h`,
      text: 'slutfört men ännu ogodkänt arbete räknas med',
      measure: 'Utnyttjande',
      kind: 'ctx',
    });

  if (surveysSent > 0)
    gaps.push({
      value: pct((satisfaction.totalResponses / surveysSent) * 100),
      text: `svarsfrekvens på kundenkäten (${nf(satisfaction.totalResponses)} svar av ${nf(surveysSent)})`,
      measure: 'Kundnöjdhet',
      kind: 'ctx',
    });

  if (leadTime.cars > 0 && leadTime.maxHours > 24 * 14)
    gaps.push({
      value: `${nf(leadTime.maxHours)} h`,
      text: `längsta enskilda ledtid — ${nf(leadTime.maxHours / 24)} dygn för en enskild bil; den flyttar inte medianen, men den stod still hela tiden`,
      measure: 'Bilar på transport',
      kind: 'ctx',
    });

  // Samma luckor som Word-uttaget (ExportWordHelper.BuildGaps), i samma ordning.
  const bookedTo = data.bookedToChanges;
  if (bookedTo.withoutReason > 0)
    gaps.push({
      value: nf(bookedTo.withoutReason),
      text: 'ombokade ärenden saknar orsak och ligger på ”Orsak saknas”',
      measure: 'Ändrade bokningstider',
      kind: 'ctx',
    });
  if (bookedTo.corrections > 0)
    gaps.push({
      value: nf(bookedTo.corrections),
      text: 'rättningar av felinlagd tid räknas inte som ombokningar',
      measure: 'Ändrade bokningstider',
      kind: 'out',
    });
  if (bookedTo.nonCustomerFacingChanges > 0)
    gaps.push({
      value: nf(bookedTo.nonCustomerFacingChanges),
      text: 'ändrade tider på uppdrag som inte räknas (t.ex. Annons, auktion, mellanlandningsben)',
      measure: 'Ändrade bokningstider',
      kind: 'out',
    });
  if (bookedTo.systemChanges > 0)
    gaps.push({
      value: nf(bookedTo.systemChanges),
      text: 'tider flyttades automatiskt (ruttplanering, förfrågan, utfyllnad, spegling till inbyte) och räknas inte',
      measure: 'Ändrade bokningstider',
      kind: 'out',
    });
  if (bookedTo.casesRebookedThreeOrMore > 0)
    gaps.push({
      value: nf(bookedTo.casesRebookedThreeOrMore),
      text: 'ärenden ombokades tre gånger eller fler',
      measure: 'Ändrade bokningstider',
      kind: 'ctx',
    });

  return (
    <Panel $span={12} $flag={gaps.some((g) => g.kind === 'stop')}>
      <Head
        tag="KTRL"
        title="Underlagets luckor"
        unit="läs siffrorna ovan med de här förbehållen"
      />
      {gaps.length === 0 ? (
        <Note>Inga luckor i underlaget för den här perioden.</Note>
      ) : (
        <Gaps>
          {gaps.map((g, i) => (
            // Listan byggs i fast ordning varje render, så indexet är en stabil nyckel.
            // Tidigare nyckel (punkt + tal + typ) kolliderade så fort två luckor på samma
            // punkt råkade ha samma tal.
            // eslint-disable-next-line react/no-array-index-key
            <Gap key={`${g.measure}-${g.kind}-${i}`}>
              <div className="g-num">{g.value}</div>
              <div className="g-text">
                {g.text}{' '}
                <span style={{ color: 'var(--ink-3)', fontFamily: 'ui-monospace, monospace' }}>
                  {g.measure}
                </span>
                <br />
                <span className={`g-chip ${g.kind}`}>{GAP_LABEL[g.kind]}</span>
              </div>
            </Gap>
          ))}
        </Gaps>
      )}
      <Note>
        <strong>Blockerar</strong> = talet går inte att använda som det ser ut nu.{' '}
        <strong>Utanför talet</strong> = raderna ingår inte i siffran de står under.{' '}
        <strong>Bakgrund</strong> = de påverkar hur den ska läsas.
      </Note>
    </Panel>
  );
};

/**
 * Trendetiketten på en av rapportens fyra rubrikbrickor.
 *
 * Saknas jämförelsen visas ingenting alls — en tom etikett är bättre än en påhittad nolla.
 */
export const HeadlineTrend = ({
  measureKey,
  data,
  previous,
  comparisonLabel,
}: {
  measureKey: string;
  data: ReportData;
  previous: ReportData | null;
  comparisonLabel: string;
}) => {
  const measure = MEASURES.find((m) => m.key === measureKey);
  const trend = measure ? trendFor(measure, data, previous) : null;
  if (!trend) return null;
  return (
    <div className="hl-trend">
      <Delta $tone={trend.tone}>
        <span>{trend.arrow}</span>
        {trend.text}
      </Delta>
      <span>mot {comparisonLabel}</span>
    </div>
  );
};

/* ---------------- jämförelsepanel ---------------- */

/**
 * Hela rapportens tal bredvid jämförelseperiodens.
 *
 * Panelen finns för att en utskrift är statisk: läsaren kan inte växla period för att se om
 * 76 % är bra eller dåligt. Utan en jämförelse i samma bild är varje tal en punkt utan riktning.
 */
export const TrendPanel = ({
  data,
  previous,
  comparisonLabel,
}: {
  data: ReportData;
  previous: ReportData | null;
  comparisonLabel: string;
}) => (
  <Panel $span={12}>
    <Head
      tag="TREND"
      title={`Jämfört med ${comparisonLabel}`}
      unit={previous ? undefined : 'jämförelsen kunde inte hämtas'}
    />
    {previous ? (
      <>
        <TableWrap>
          <TrendTable>
            <thead>
              <tr>
                <th>Mått</th>
                <th className="n">Perioden</th>
                <th className="n">Jämförelse</th>
                <th className="n">Förändring</th>
              </tr>
            </thead>
            <tbody>
              {MEASURES.map((m) => {
                const now = m.value(data);
                const before = m.value(previous);
                const trend = trendOf(m, now, before);
                return (
                  <tr key={m.key}>
                    <td className="name">
                      {m.label}
                      {m.hint ? <small>{m.hint}</small> : null}
                    </td>
                    <td className="n">{now === null ? '–' : m.format(now)}</td>
                    <td className="n">{before === null ? '–' : m.format(before)}</td>
                    <td className="n">
                      {trend ? (
                        <Delta $tone={trend.tone}>
                          <span>{trend.arrow}</span>
                          {trend.text}
                        </Delta>
                      ) : (
                        '–'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TrendTable>
        </TableWrap>
        <Note>
          Färgen säger om rörelsen är bra, pilen vilket håll talet gick — de är inte samma sak.
          Kortare ledtid och färre avbokningar är förbättringar nedåt. Andelar och betyg jämförs i
          procentenheter (<strong>pe</strong>), antal och timmar i procent. Ett streck betyder att
          måttet saknas i någon av perioderna, eller att jämförelseperioden var noll — då finns
          ingen procentuell förändring att räkna.
        </Note>
      </>
    ) : (
      <Note>
        Jämförelseperioden kunde inte hämtas, så talen ovan står utan trend. Ladda om sidan för
        att försöka igen.
      </Note>
    )}
  </Panel>
);

/* ---------------- årsrapport ---------------- */

/**
 * Årsrapportens jämförelse: årets tal bredvid samma period i fjol, och bredvid de två senaste
 * rapportveckorna.
 *
 * Fjolåret säger om året går bättre eller sämre. Veckorna säger om det senaste avviker från
 * året — och det syns bara om årets antal räknas om till ett snitt per vecka, eftersom ett års
 * levererade bilar och en veckas inte går att läsa bredvid varandra. Andelar, timmar och betyg
 * står som de är: de beror inte på periodens längd.
 */
export const YearOverviewPanel = ({
  data,
  previous,
  weeks,
  periodStart,
  periodEnd,
  comparisonLabel,
}: {
  data: ReportData;
  previous: ReportData | null;
  weeks: WeekComparison;
  periodStart: Date;
  periodEnd: Date;
  comparisonLabel: string;
}) => {
  const weeksInYear = Math.max(1, (periodEnd.getTime() - periodStart.getTime()) / (7 * DAY_MS));
  const value = (m: Measure, d: ReportData | null) => (d ? m.value(d) : null);
  const show = (m: Measure, v: number | null) => (v === null ? '–' : m.format(v));

  return (
    <Panel $span={12}>
      <Head tag="ÅR" title="Året mot fjolåret och de senaste veckorna" />
      <TableWrap>
        <TrendTable>
          <thead>
            <tr>
              <th>Mått</th>
              <th className="n">Året</th>
              <th className="n">I fjol</th>
              <th className="n">Förändring</th>
              <th className="n">Rapportveckan</th>
              <th className="n">Föregående vecka</th>
              <th className="n">Förändring</th>
            </tr>
          </thead>
          <tbody>
            {MEASURES.map((m) => {
              const year = m.value(data);
              const lastYear = value(m, previous);
              const week = value(m, weeks.current);
              const weekBefore = value(m, weeks.previous);
              const yearTrend = trendOf(m, year, lastYear);
              const weekTrend = trendOf(m, week, weekBefore);
              return (
                <tr key={m.key}>
                  <td className="name">
                    {m.label}
                    {m.hint ? <small>{m.hint}</small> : null}
                  </td>
                  <td className="n">
                    {show(m, year)}
                    {m.isCount && year !== null ? (
                      <small> · {nf(year / weeksInYear, 1)}/v</small>
                    ) : null}
                  </td>
                  <td className="n">{show(m, lastYear)}</td>
                  <td className="n">
                    <RowDelta trend={yearTrend} />
                  </td>
                  <td className="n">{show(m, week)}</td>
                  <td className="n">{show(m, weekBefore)}</td>
                  <td className="n">
                    <RowDelta trend={weekTrend} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TrendTable>
      </TableWrap>
      <Note>
        Året jämförs med {comparisonLabel}, rapportveckan med veckan innan. Antal står också som
        snitt per vecka (<strong>/v</strong>) så att de går att ställa bredvid veckornas tal.
        Färgen säger om rörelsen är bra, pilen vilket håll talet gick. Andelar och betyg jämförs i
        procentenheter (<strong>pe</strong>), antal och timmar i procent. Ett streck betyder att
        måttet saknas, eller att perioden inte gick att hämta.
      </Note>
    </Panel>
  );
};

/* ---------------- metod ---------------- */

const DEFINITIONS: [string, string][] = [
  [
    'Period',
    'Rapportveckan går fredag 00:00 till och med torsdag 23:59. Perioden står i huvudet på sidan.',
  ],
  [
    'När är en bil levererad?',
    'När sista uppdraget är godkänt. Ärendet stängs då automatiskt och den tidpunkten är vad allt i rapporten dateras efter. Ett ärende = en bil.',
  ],
  [
    'Vad räknas inte',
    'Fälttestarens egen resa till kunden (tåg, buss, taxi) är inte bilen på transport. Lastbilens eget ärende är körningen, inte en levererad bil.',
  ],
  [
    'Auktion',
    'Redovisas som Inköp, men hålls separerbart i underlaget om ledningen vill dela upp dem senare.',
  ],
  [
    'Annons',
    'Enbart interna leveranser. Räknas per ärende, inte per uppdrag, så talet stämmer mot annonsraden i levererade bilar.',
  ],
  [
    'Ledtid på transport',
    'Klocktid från första upphämtningen till avlämningen på logistikanläggningen i Strängnäs. Helger och nätter räknas med. Körtid är summan av etappernas egen längd.',
  ],
  [
    'Median, inte snitt',
    'Transporttiderna redovisas som median — den mittersta bilen — tills vidare. Ett snitt dras upp av de enstaka bilar som blivit stående i veckor och säger då mer om dem än om hur det går, så det visas inte i rapporten alls. Snitt och längsta ledtid finns kvar per län och ärendetyp i Excel-underlaget. Körtiden är ett medelvärde per bil; etapptiderna har ingen median. Kundnöjdheten är fortsatt ett snittbetyg.',
  ],
  [
    'Arbetstidstak',
    'Timmar per heltidsvecka gånger sysselsättningsgrad, minskat pro rata med godkänd frånvaro. Veckovila enligt körpolicyn räknas inte som frånvaro. Taket räknas bara för perioder där fälttestaren var i tjänst, dvs. har tillgänglighetspost eller uppdrag — hela kåren är inte i tjänst hela tiden.',
  ],
  [
    'Återöppnade ärenden',
    'Ett ärende som öppnas igen får ny stängningstidpunkt och byter period. Fakturerade ärenden är låsta, så historiken är stabil bakåt.',
  ],
];

export const MethodPanel = () => (
  <Panel $span={12}>
    <Head tag="METOD" title="Så är rapporten räknad" unit="definitionerna bakom varje tal ovan" />
    <Definitions>
      {DEFINITIONS.map(([term, text]) => (
        <dl key={term}>
          <dt>{term}</dt>
          <dd>{text}</dd>
        </dl>
      ))}
    </Definitions>
    <Note>
      Fullständigt underlag — varje period, varje län och varje lastbil — finns i Excel-filen.
      Den här sidan visar rader och trender; filen visar allt.
    </Note>
  </Panel>
);
